import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Button, CircularProgress } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ConsoleHeader, {
  ConsoleHeaderMeta,
  LedgerFrame,
} from '../components/common/ConsoleHeader.tsx';
import { designTokens } from '../theme.ts';

const fmt = (n) => Number.isFinite(Number(n)) ? new Intl.NumberFormat('ko-KR').format(Number(n)) : '—';

export default function ProductRequirementRent() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchRequests(); }, []);

  const fetchRequests = async () => {
    try {
      const res = await axios.get('/api/product-requirements');
      setRequests(res.data ?? []);
    } catch (e) {
      console.error('Failed to load data', e);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = (id) => {
    window.open(`/api/product-requirements/${id}/excel`);
  };

  const total = requests.length;

  return (
    <Box sx={{ fontFamily: designTokens.fonts.sans }}>
      <ConsoleHeader
        code="MARS · PROD · RENT-CONTRACTS"
        title="생산요청서"
        meta={<ConsoleHeaderMeta recordCountLabel="생산요청" count={total} />}
      />

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 8 }}>
          <CircularProgress size={28} sx={{ color: designTokens.cargo }} />
          <Box sx={{ fontFamily: designTokens.fonts.sans, fontSize: 14, color: '#6B7178' }}>
            생산요청 목록을 불러오는 중입니다…
          </Box>
        </Box>
      ) : (
        <LedgerFrame label="임대계약 원장" rows={total}>
          <Box sx={{ overflowX: 'auto' }}>
            <Box
              component="table"
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                fontFamily: designTokens.fonts.sans,
                '& th': {
                  textAlign: 'left',
                  padding: '10px 14px',
                  fontFamily: designTokens.fonts.sans,
                  fontWeight: 600,
                  fontSize: 13,
                  letterSpacing: 0,
                  color: '#6B7178',
                  bgcolor: '#F9FAFB',
                  borderBottom: '1px solid #E4E7EA',
                  whiteSpace: 'nowrap',
                },
                '& td': {
                  padding: '10px 14px',
                  fontSize: 13.5,
                  color: '#171A1E',
                  borderBottom: '1px solid #EDEFF1',
                },
                '& tr:hover td': { bgcolor: '#F2F4F5' },
              }}
            >
              <thead>
                <tr>
                  <th>계약명</th>
                  <th>업체명</th>
                  <th>현장명</th>
                  <th style={{ textAlign: 'right' }}>총액 (₩)</th>
                  <th style={{ width: 140 }}>액션</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((req) => (
                  <tr key={req.id}>
                    <td style={{ fontWeight: 600 }}>{req.contractName || '—'}</td>
                    <td>{req.companyName || '—'}</td>
                    <td style={{ color: '#6B7178' }}>{req.siteName || '—'}</td>
                    <td style={{
                      textAlign: 'right',
                      fontFamily: designTokens.fonts.mono,
                      fontVariantNumeric: 'tabular-nums',
                      fontWeight: 700,
                    }}>
                      {fmt(req.totalAmount)}
                    </td>
                    <td>
                      <Button
                        size="small"
                        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
                        onClick={() => downloadExcel(req.id)}
                        sx={{
                          fontFamily: designTokens.fonts.sans,
                          fontWeight: 600,
                          fontSize: 12,
                          borderRadius: '8px',
                          border: '1px solid #E4E7EA',
                          color: '#171A1E',
                          bgcolor: '#FFFFFF',
                          px: 1.25,
                          py: 0.4,
                          '&:hover': { bgcolor: '#F2F4F5', borderColor: '#D7DBDF' },
                        }}
                      >
                        엑셀
                      </Button>
                    </td>
                  </tr>
                ))}
                {requests.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: '#6B7178', padding: '32px 0' }}>
                      <Box sx={{ fontFamily: designTokens.fonts.sans, fontSize: 14 }}>
                        등록된 계약이 없습니다
                      </Box>
                    </td>
                  </tr>
                )}
              </tbody>
            </Box>
          </Box>
        </LedgerFrame>
      )}
    </Box>
  );
}
